import {
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import Checkbox from "@material-ui/core/Checkbox"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import FormGroup from "@material-ui/core/FormGroup"
import CloseIcon from "@material-ui/icons/Close"
import EditIcon from "@material-ui/icons/Edit"
import FileCopyIcon from "@material-ui/icons/FileCopy"
import PropTypes from "prop-types"
import { useContext, useEffect } from "react"
import { ChromePicker } from "react-color"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { useQueryClient } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import CounterHeader from "~/components/Common/CounterHeader"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import ClientContext from "~/contexts/Client"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "../../Common/Box"
import AppTheme from "../AppTheme"
import useStyles from "./styles"

const ClientDetails = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const phoneView = useMediaQuery(theme.breakpoints.down("sm"))

  const { openSnackBar } = useContext(AppContext)
  const {
    client,
    setDebouncedDetails,
    debouncedDetails,
    clientDetailsValues,
    setClientDetailsValues,
    setInitialDetailsContext,
    lengthError,
    tryToSave,
    updateClientMutation,
    setShowPicker,
    showPicker,
  } = useContext(ClientContext)
  const debouncedLocalValues = useDebounce(clientDetailsValues, 200)
  const openErrorSnackbar = useOpenErrorSnackbar()

  // set client details to component state
  useEffect(() => {
    if (client?.id) {
      const clientData = {
        name: client?.name,
        domain: client?.domain || "",
        brand_color: client?.brand_color || "#0078F6",
        user_responses: client?.user_responses ? true : false,
        user_stats: client?.user_stats ? true : false,
        time_to_proficiency: client?.time_to_proficiency ? true : false,
        kc_and_final: client?.kc_and_final ? true : false,
        sentiment_evaluation: client?.sentiment_evaluation ? true : false,
        cohort_course_analysis: client?.cohort_course_analysis ? true : false,
        persona_insight: client?.persona_insight ? true : false,
        secret_saml_key: client?.secret_saml_key || "",
        saml_idp_name: client?.saml_idp_name || "",
        stats_users_only: client?.stats_users_only ? true : false,
        use_client_courses_for_stats_endpoint:
          client?.use_client_courses_for_stats_endpoint ? true : false,
        include_questions_answer_client_stats:
          client?.include_questions_answer_client_stats ? true : false,
        stats_endpoint_key: client?.stats_endpoint_key || "",
        dashboard_visible: client?.dashboard_visible ? true : false,
        dashboard_description: client?.dashboard_description || "",
        dashboard_video: client?.dashboard_video || null,
        dashboard_image: client?.dashboard_image || null,
        app_subdomain: client?.app_subdomain || null,
        app_theme: client?.app_theme || null,
        login_url: client?.login_url || null,
      }
      setClientDetailsValues({ ...clientData })
      setInitialDetailsContext({ ...clientData })
    }
  }, [client])

  const nameError =
    ((debouncedDetails?.name === "" || debouncedDetails?.name?.length > 50) &&
      tryToSave) ||
    (lengthError && debouncedDetails?.name?.length > 50)
  const samlError =
    ((debouncedDetails?.secret_saml_key === "" ||
      debouncedDetails?.secret_saml_key?.length > 50) &&
      tryToSave) ||
    (lengthError && debouncedDetails?.secret_saml_key?.length > 50)
  const samlNameError =
    ((debouncedDetails?.saml_idp_name === "" ||
      debouncedDetails?.saml_idp_name?.length > 20) &&
      tryToSave) ||
    (lengthError && debouncedDetails?.saml_idp_name?.length > 20)

  const brandColorError = clientDetailsValues?.brand_color === "" && tryToSave

  const availableAnalyticsError =
    clientDetailsValues?.availableAnalytics?.length === 0 && tryToSave
  const availableAnalyticsList = [
    {
      label: "User Stats",
      value: "user_stats",
    },
    {
      label: "Time to Proficiency",
      value: "time_to_proficiency",
    },
    {
      label: "Knowledge Check & Final Assessment",
      value: "kc_and_final",
    },
    {
      label: "Sentiment Evaluation",
      value: "sentiment_evaluation",
    },
    {
      label: "User Responses",
      value: "user_responses",
    },
    {
      label: "Cohort Course Analysis",
      value: "cohort_course_analysis",
    },
    {
      label: "Persona Insight",
      value: "persona_insight",
    },
    { label: "Show Stats for Client Users Only", value: "stats_users_only" },
  ]

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image =
      uploadRes === null
        ? null
        : JSON.stringify({
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            originial_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          })
    const data = {
      ...clientDetailsValues,
      logo: image,
    }
    updateClientMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("client", res.data).then((serializedData) => {
          return queryClient.setQueryData("clients", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                return serializedData
              }
            })
            return newArray
          })
        })
        setTimeout(() => {
          loadingAction(false)
        }, 400)
      },
      onError: (err) => {
        loadingAction(false)
        console.error(err)
        openErrorSnackbar(err, "Error Updating Logo Image")
      },
    })
  }

  const setCertificationLogo = (uploadRes, loadingAction, type) => {
    const image =
      uploadRes === null
        ? null
        : JSON.stringify({
            url: uploadRes.data.url,
            secure_url: uploadRes.data.secure_url,
            originial_filename: uploadRes.data.original_filename,
            version: uploadRes.data.version,
            format: uploadRes.data.format,
            public_id: uploadRes.data.public_id,
          })
    const data = {
      ...clientDetailsValues,
      certification_logo: image,
    }
    updateClientMutation.mutate(data, {
      onSuccess: (res) => {
        serialize("client", res.data).then((serializedData) => {
          return queryClient.setQueryData("clients", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                return serializedData
              }
            })
            return newArray
          })
        })
        setTimeout(() => {
          loadingAction(false)
        }, 400)
      },
      onError: (err) => {
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Hero Image")
      },
    })
  }

  //set details context state from this component state using debounce
  useEffect(() => {
    setDebouncedDetails(clientDetailsValues)
  }, [debouncedLocalValues])

  const renderCertificationLogo = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        <Box width={phoneView ? "100%" : "48%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Certification Logo
          </Typography>
          <ThumbnailUpload
            title="Drop client certification logo here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={
              client?.certification_logo
                ? JSON.parse(client.certification_logo)
                : {}
            }
            onUpload={setCertificationLogo}
            type="question"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3" color="primary" className={classes.title}>
          Client Details
        </Typography>
        <Typography variant="h3" color="primary" className={classes.title}>
          {`ID: ${client?.id}`}
        </Typography>
      </Box>
      <CounterHeader
        title="Client Name"
        value={debouncedDetails?.name}
        max={50}
      />
      <TextField
        value={clientDetailsValues?.name || ""}
        onChange={(e) => {
          setClientDetailsValues((oldState) => ({
            ...oldState,
            name: e.target.value,
          }))
        }}
        style={{ marginBottom: "10px" }}
        InputProps={{
          classes: {
            root: classes.inputBackgroundColor,
          },
        }}
        variant="filled"
        size="small"
        fullWidth
      />
      {nameError ? (
        <Typography className={classes.requiredText}>
          Name is required and it has to be less than 50 characters
        </Typography>
      ) : null}
      <Box width="100%" marginTop="8px">
        <Typography color="secondary" className={classes.inputLabel}>
          Client Domain (comma separate multiple domains)
        </Typography>
        <TextField
          value={clientDetailsValues?.domain || ""}
          onChange={(e) => {
            setClientDetailsValues((oldState) => ({
              ...oldState,
              domain: e.target.value,
            }))
          }}
          style={{ marginBottom: "10px" }}
          InputProps={{
            classes: {
              root: classes.inputBackgroundColor,
            },
          }}
          variant="filled"
          size="small"
          fullWidth
        />
      </Box>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
        <Box width={phoneView ? "100%" : "48%"}>
          <Typography color="secondary" className={classes.inputLabel}>
            Logo
          </Typography>
          <ThumbnailUpload
            title="Drop client logo here"
            cloudinaryUploadPreset={
              import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
            }
            value={client?.logo || {}}
            onUpload={setThumbnail}
            type="client"
          />
        </Box>

        <Box width={phoneView ? "100%" : "48%"} position="relative">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography color="secondary" className={classes.inputLabel}>
              Brand Spot Color (HEX Code)
            </Typography>
            <IconButton onClick={() => setShowPicker((oldState) => !oldState)}>
              {!showPicker ? (
                <EditIcon fontSize="small" />
              ) : (
                <CloseIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <TextField
            disabled
            value={clientDetailsValues?.brand_color || ""}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
              endAdornment: (
                <Box
                  borderRadius="50%"
                  width="15px"
                  height="15px"
                  style={{ backgroundColor: clientDetailsValues?.brand_color }}
                />
              ),
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {showPicker && (
            <Box position="absolute" right={0} top={50}>
              <ChromePicker
                disableAlpha
                color={clientDetailsValues?.brand_color}
                onChange={(value) => {
                  setClientDetailsValues((oldState) => ({
                    ...oldState,
                    brand_color: value?.hex,
                  }))
                }}
              />
            </Box>
          )}
          {brandColorError ? (
            <Typography className={classes.requiredText}>
              Select a brand color is required
            </Typography>
          ) : null}
        </Box>
      </Box>
      {renderCertificationLogo()}
      <Box width="100%" marginTop="8px">
        <Typography color="secondary" className={classes.inputLabel}>
          Available Analytics
        </Typography>
        <FormGroup>
          {availableAnalyticsList?.map((x, i) => {
            return (
              <Box key={i}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={clientDetailsValues?.[x.value] ?? false}
                      onChange={() => {
                        setClientDetailsValues((oldState) => ({
                          ...oldState,
                          [x.value]: !clientDetailsValues?.[x.value],
                        }))
                      }}
                      name={x.value}
                    />
                  }
                  style={
                    x.value === "user_responses"
                      ? { paddingLeft: "30px" }
                      : null
                  }
                  label={x.label}
                />
              </Box>
            )
          })}
        </FormGroup>
        {availableAnalyticsError ? (
          <Typography className={classes.requiredText}>
            Select at least one
          </Typography>
        ) : null}
      </Box>
      <Box marginTop="15px" marginBottom="15px">
        <hr />
      </Box>
      <Typography
        variant="h3"
        color="primary"
        className={classes.title}
        style={{ marginBottom: 10 }}
      >
        Client Stats Endpoint
      </Typography>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                clientDetailsValues?.use_client_courses_for_stats_endpoint ??
                false
              }
              onChange={() => {
                setClientDetailsValues((oldState) => ({
                  ...oldState,
                  use_client_courses_for_stats_endpoint:
                    !clientDetailsValues?.use_client_courses_for_stats_endpoint,
                }))
              }}
              name="use_client_courses_for_stats_endpoint"
            />
          }
          label="Use Client Courses for Stats Endpoint"
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                clientDetailsValues?.include_questions_answer_client_stats ??
                false
              }
              onChange={() => {
                setClientDetailsValues((oldState) => ({
                  ...oldState,
                  include_questions_answer_client_stats:
                    !clientDetailsValues?.include_questions_answer_client_stats,
                }))
              }}
              name="include_questions_answer_client_stats"
            />
          }
          label="Include Questions and Answers in Stats Download"
        />
      </Box>
      <Box width="100%" marginTop="8px">
        <CounterHeader
          title="Client Stats Endpoint Key"
          value={debouncedDetails?.stats_endpoint_key}
          max={32}
        />
        <TextField
          value={clientDetailsValues?.stats_endpoint_key || ""}
          onChange={(e) => {
            setClientDetailsValues((oldState) => ({
              ...oldState,
              stats_endpoint_key: e.target.value,
            }))
          }}
          style={{ marginBottom: "10px" }}
          InputProps={{
            classes: {
              root: classes.inputBackgroundColor,
            },
          }}
          variant="filled"
          size="small"
          fullWidth
        />
      </Box>
      <Box marginTop="15px" marginBottom="15px">
        <hr />
      </Box>
      <Typography
        variant="h3"
        color="primary"
        className={classes.title}
        style={{ marginBottom: 10 }}
      >
        SAML
      </Typography>
      <Box width="100%" marginTop="8px">
        <CounterHeader
          title="SAML Secret Key"
          value={debouncedDetails?.secret_saml_key}
          max={50}
        />
        <TextField
          value={clientDetailsValues?.secret_saml_key || ""}
          onChange={(e) => {
            setClientDetailsValues((oldState) => ({
              ...oldState,
              secret_saml_key: e.target.value,
            }))
          }}
          style={{ marginBottom: "10px" }}
          InputProps={{
            classes: {
              root: classes.inputBackgroundColor,
            },
          }}
          variant="filled"
          size="small"
          fullWidth
        />
        {samlError ? (
          <Typography className={classes.requiredText}>
            Secret Key is required and it has to be less than 50 characters
          </Typography>
        ) : null}
      </Box>
      {/* idp start */}
      <Box width="100%" marginTop="8px">
        <CounterHeader
          title="SAML IDP Name"
          value={debouncedDetails?.saml_idp_name}
          max={20}
        />
        <TextField
          value={clientDetailsValues?.saml_idp_name || ""}
          onChange={(e) => {
            setClientDetailsValues((oldState) => ({
              ...oldState,
              saml_idp_name: e.target.value,
            }))
          }}
          style={{ marginBottom: "10px" }}
          InputProps={{
            classes: {
              root: classes.inputBackgroundColor,
            },
          }}
          variant="filled"
          size="small"
          fullWidth
        />
        {samlNameError ? (
          <Typography className={classes.requiredText}>
            SAML Name is required and it has to be less than 20 characters
          </Typography>
        ) : null}
      </Box>
      {/* start line */}
      {client?.saml_idp_name && (
        <>
          <Typography
            color="secondary"
            className={classes.title}
            style={{ marginBottom: "4px" }}
          >
            SAML Login Url
          </Typography>
          <CopyToClipboard
            text={`${import.meta.env.VITE_API_ENDPOINT}/app/saml/${
              client?.saml_idp_name
            }/auth/`}
            onCopy={() => openSnackBar({ message: "Link copied" })}
            options={{ format: "text/plain" }}
          >
            <Box
              style={{ cursor: "pointer" }}
              width="100%"
              marginBottom="8px"
              display="flex"
              alignItems="center"
            >
              <Typography
                variant="h3"
                color="primary"
                className={classes.linkText}
              >
                {import.meta.env.VITE_API_ENDPOINT}/app/saml/
                {client?.saml_idp_name}
                /auth/
              </Typography>
              <FileCopyIcon
                variant="contained"
                // disableElevation
                style={{ marginLeft: "10px", width: "14px", pointer: "cursor" }}
              />
            </Box>
          </CopyToClipboard>
          <Typography
            color="secondary"
            className={classes.title}
            style={{ marginBottom: "4px" }}
          >
            SAML Metadata Url
          </Typography>
          <CopyToClipboard
            text={`${import.meta.env.VITE_API_ENDPOINT}/app/saml/${
              client?.saml_idp_name
            }/metadata/`}
            onCopy={() => openSnackBar({ message: "Link copied" })}
            options={{ format: "text/plain" }}
          >
            <Box
              width="100%"
              marginBottom="8px"
              display="flex"
              alignItems="center"
              style={{ cursor: "pointer" }}
            >
              <Typography variant="h3" className={classes.linkText}>
                {import.meta.env.VITE_API_ENDPOINT}/app/saml/
                {client?.saml_idp_name}
                /metadata/
              </Typography>
              <FileCopyIcon
                variant="contained"
                // disableElevation
                style={{ marginLeft: "10px", width: "14px" }}
              />
            </Box>
          </CopyToClipboard>
        </>
      )}
      <AppTheme />
    </Box>
  )
}

ClientDetails.propTypes = {
  courseId: PropTypes.string,
  tags: PropTypes.array,
  categories: PropTypes.array,
}

export default ClientDetails
